/**
 * Hero carousel (MDL): slide transitions and autoplay rules for a hero that fills the screen.
 *
 * TRANSITIONS (data-transition on the slideshow): the theme's slideshow moves between slides by
 * scrolling a horizontal scroller, which is the "Slide" option and stays stock. For fade, slide up,
 * slide down and slide off, the section's stylesheet stacks every slide in the same grid cell and
 * this script animates the outgoing and incoming slides with the Web Animations API over the
 * "Transition takes" setting (data-transition-duration, seconds). The stock component keeps doing
 * what it does well (arrows, dots, counter, pause and play buttons, editor block selection, the
 * slideshow:select event) through instance overrides of the few methods that assume scrolling.
 *
 * AUTOPLAY (autoplay attribute): the theme's slideshow pauses while the pointer is over it, keeps
 * rotating when scrolled out of view, and stays paused for good after a drag or a tab switch. On a
 * full-screen hero the pointer is nearly always over it, so slides after the first were never seen.
 * Here, only when the section's autoplay is on:
 *  - a parked pointer does not pause; a moving one does, and rotation resumes once the pointer has
 *    been still for the "pointer still for" delay (data-resume-delay, seconds) or has left;
 *  - rotation runs only while at least half of the carousel is in view and the tab is visible;
 *  - a swipe, a drag, an arrow or a dot restarts the countdown instead of leaving autoplay paused;
 *  - a slide whose video is playing stays until the video has played through once (a video that
 *    is not loaded, or does not start or stalls, does not hold the carousel);
 *  - keyboard focus inside pauses (a mouse click does not); the pause and play buttons keep their stock behaviour;
 *  - visitors who prefer reduced motion get no autoplay and no animated transition.
 */
import { SlideshowSelectEvent } from '@theme/events';
import { prefersReducedMotion, viewTransition } from '@theme/utilities';

const SELECTOR = 'slideshow-component.mdl-hero-carousel';
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
const SWIPE_DISTANCE = 40; // px of horizontal travel that counts as a swipe
const SWIPE_VELOCITY = 0.5; // px per ms that counts as a flick
const HOLD_START_GRACE = 8000; // ms a loaded video gets to start playing before autoplay moves on without it
const HOLD_STALL = 5000; // ms without playback progress after which a held slide is released

/**
 * Keyframes for each transition. `dir` is 1 going forwards (next) and -1 going backwards (previous).
 * `top` names the slide that paints above the other while they animate.
 * @type {Record<string, (dir: number) => { in?: Keyframe[], out?: Keyframe[], top: 'in' | 'out' }>}
 */
const TRANSITIONS = {
  // The incoming slide fades in over the outgoing one.
  fade: () => ({
    in: [{ opacity: 0 }, { opacity: 1 }],
    top: 'in',
  }),
  // Both slides move up together: the incoming one pushes the outgoing one out of the top.
  slide_up: (dir) => ({
    in: [{ transform: `translateY(${dir * 100}%)` }, { transform: 'translateY(0)' }],
    out: [{ transform: 'translateY(0)' }, { transform: `translateY(${dir * -100}%)` }],
    top: 'in',
  }),
  // Both slides move down together.
  slide_down: (dir) => ({
    in: [{ transform: `translateY(${dir * -100}%)` }, { transform: 'translateY(0)' }],
    out: [{ transform: 'translateY(0)' }, { transform: `translateY(${dir * 100}%)` }],
    top: 'in',
  }),
  // The outgoing slide moves off to the left (right when going back) and uncovers the incoming one.
  slide_off: (dir) => ({
    out: [{ transform: 'translateX(0)' }, { transform: `translateX(${dir * -100}%)` }],
    top: 'out',
  }),
};

/** @param {HTMLElement & Record<string, any>} el */
async function setup(el) {
  if (el.dataset.mdlSetup) return;
  // 'pending' while this runs; the stylesheet shows only the first slide until it reads 'on'.
  el.dataset.mdlSetup = 'pending';

  // The stock component waits for a page transition before it sets itself up; do the same so the
  // overrides below land after its listeners, not before.
  if (viewTransition.current) {
    await viewTransition.current;
    if (!el.isConnected) return;
  }

  if (el.dataset.transition) setupTransition(el);
  if (el.hasAttribute('autoplay')) setupAutoplay(el);
  el.dataset.mdlSetup = 'on';
}

/**
 * Stacked transitions: fade, slide up, slide down, slide off.
 * @param {HTMLElement & Record<string, any>} el
 */
function setupTransition(el) {
  const frames = TRANSITIONS[el.dataset.transition || ''];
  if (!frames) return;
  const duration = Math.max(0, parseFloat(el.dataset.transitionDuration || '0.5') || 0) * 1000;

  // The stock component measures which slides are in the scroller's viewport. Stacked, every slide
  // would count, so next() would jump by all of them and a resize would unhide them all. Only the
  // current slide is ever "visible" here.
  Object.defineProperty(el, 'visibleSlides', {
    configurable: true,
    get() {
      const slide = el.slides?.[el.current];
      return slide ? [slide] : [];
    },
  });

  // The stock mouse drag scrolls the scroller; there is nothing to scroll, so turn it off. It reads
  // this getter (the disabled attribute would hide the arrows); the setter absorbs the stock setup's
  // own assignment.
  Object.defineProperty(el, 'disabled', {
    configurable: true,
    get: () => true,
    set() {},
  });

  // Only the current slide may be aria-hidden="false" (the stylesheet shows slides by that attribute).
  // The stock setup measures visible slides in one frame and writes aria-hidden in the next, so when
  // this script lands between the two, its stale write marks every stacked slide visible. Put the
  // attributes right now and again after any write that disagrees; the observer runs before paint.
  const enforce = () => {
    el.slides?.forEach((slide, index) => {
      if (slide.hasAttribute('data-animating')) return;
      const value = index === el.current ? 'false' : 'true';
      if (slide.getAttribute('aria-hidden') !== value) slide.setAttribute('aria-hidden', value);
    });
  };
  enforce();
  const observer = new MutationObserver(enforce);
  el.slides?.forEach((slide) => observer.observe(slide, { attributes: true, attributeFilter: ['aria-hidden'] }));

  /** @type {(() => void) | undefined} Finishes the transition in flight, instantly. */
  let settle;
  const finish = () => {
    const pending = settle;
    settle = undefined;
    pending?.();
  };

  /**
   * @param {number | string | { id: string }} input
   * @param {Event} [event]
   * @param {{ animate?: boolean }} [options]
   */
  el.select = (input, event, options = {}) => {
    const slides = el.slides;
    if (!slides?.length) return;

    let requested;
    if (typeof input === 'number') requested = input;
    else if (typeof input === 'string') requested = parseInt(input, 10);
    else if (input && 'id' in input) {
      requested = slides.findIndex((slide) => slide.getAttribute('slide-id') == input.id);
      if (requested === -1) return;
    }
    if (requested === undefined || Number.isNaN(requested)) return;

    const last = slides.length - 1;
    const from = el.current;
    if (!el.infinite) requested = Math.min(Math.max(requested, 0), last);
    const to = requested < 0 ? last : requested > last ? 0 : requested;
    const outgoing = slides[from];
    const incoming = slides[to];
    if (!incoming || incoming === outgoing) return;

    event?.preventDefault();
    // Direction comes from the request before wrapping, so previous from the first slide goes backwards.
    const dir = requested < from ? -1 : 1;
    const instant = duration === 0 || options.animate === false || prefersReducedMotion();

    finish();

    incoming.setAttribute('aria-hidden', 'false');
    el.current = to;

    const done = () => {
      outgoing.setAttribute('aria-hidden', 'true');
      outgoing.removeAttribute('data-animating');
      incoming.removeAttribute('data-animating');
    };

    if (instant) {
      done();
    } else {
      const spec = frames(dir);
      incoming.setAttribute('data-animating', spec.top === 'in' ? 'top' : 'under');
      outgoing.setAttribute('data-animating', spec.top === 'out' ? 'top' : 'under');

      const timing = { duration, easing: EASING, fill: 'forwards' };
      const animations = [];
      if (spec.in) animations.push(incoming.animate(spec.in, timing));
      if (spec.out) animations.push(outgoing.animate(spec.out, timing));

      const current = () => {
        done();
        for (const animation of animations) animation.cancel();
      };
      settle = current;
      Promise.all(animations.map((animation) => animation.finished)).then(
        () => {
          if (settle === current) finish();
        },
        () => {}
      );
    }

    el.dispatchEvent(
      new SlideshowSelectEvent({
        index: to,
        previousIndex: from,
        userInitiated: event != null,
        trigger: 'select',
        slide: incoming,
        id: incoming.getAttribute('slide-id'),
      })
    );
  };

  setupSwipe(el);
}

/**
 * Pointer swipe for the stacked layout, where there is no scroller to swipe: a horizontal pull of
 * SWIPE_DISTANCE or a quick flick changes slide; a vertical pull is left to the page.
 * @param {HTMLElement & Record<string, any>} el
 */
function setupSwipe(el) {
  const scroller = el.querySelector(':scope > slideshow-container > slideshow-slides');
  if (!(scroller instanceof HTMLElement)) return;

  /**
   * `moved`: the pointer travelled far enough in any direction to be a pull rather than a tap, so the
   * click that follows must not open the slide's link. `horizontal`: the pull is sideways, so it is
   * ours; a vertical one is left to the page (touch-action: pan-y).
   * @type {{ id: number, x: number, y: number, t: number, moved: boolean, horizontal: boolean } | undefined}
   */
  let swipe;

  /** @param {PointerEvent} event */
  const start = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('button, input, select, textarea, model-viewer')) return;
    if (event.pointerType === 'mouse') {
      if (event.button !== 0) return;
      // As the stock drag does: no text selection or image drag while pulling with the mouse.
      event.preventDefault();
    }
    swipe = { id: event.pointerId, x: event.clientX, y: event.clientY, t: performance.now(), moved: false, horizontal: false };
  };

  /** @param {PointerEvent} event */
  const move = (event) => {
    if (!swipe || event.pointerId !== swipe.id || swipe.moved) return;
    const dx = event.clientX - swipe.x;
    const dy = event.clientY - swipe.y;
    if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
    swipe.moved = true;
    swipe.horizontal = Math.abs(dx) > Math.abs(dy);
    if (swipe.horizontal) scroller.setPointerCapture(event.pointerId);
  };

  /** @param {PointerEvent} event */
  const end = (event) => {
    if (!swipe || event.pointerId !== swipe.id) return;
    const { x, t, moved, horizontal } = swipe;
    swipe = undefined;
    if (!moved) return;

    const dx = event.clientX - x;
    const velocity = Math.abs(dx) / Math.max(performance.now() - t, 1);
    if (horizontal && event.type === 'pointerup' && (Math.abs(dx) >= SWIPE_DISTANCE || velocity >= SWIPE_VELOCITY)) {
      dx < 0 ? el.next(event) : el.previous(event);
    }

    // The click that follows a pull must not open the slide's link.
    /** @param {Event} click */
    const swallow = (click) => {
      click.preventDefault();
      click.stopPropagation();
    };
    scroller.addEventListener('click', swallow, { capture: true, once: true });
    setTimeout(() => scroller.removeEventListener('click', swallow, { capture: true }), 400);
  };

  scroller.addEventListener('pointerdown', start);
  scroller.addEventListener('pointermove', move);
  scroller.addEventListener('pointerup', end);
  scroller.addEventListener('pointercancel', end);
}

/**
 * Autoplay rules.
 * @param {HTMLElement & Record<string, any>} el
 */
function setupAutoplay(el) {
  const proto = Object.getPrototypeOf(el);
  // Stop the stock timer and drop the stock hover handlers (prototype methods, so the same references).
  proto.suspend.call(el);
  el.removeEventListener('mouseenter', proto.suspend);
  el.removeEventListener('mouseleave', proto.resume);

  if (prefersReducedMotion()) {
    el.removeAttribute('autoplay');
    return;
  }

  const delay = Math.max(0, parseFloat(el.dataset.resumeDelay || '1') || 0) * 1000;
  /** @type {ReturnType<typeof setInterval> | undefined} */
  let timer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let moveTimer;
  let moving = false;
  let focused = false;
  let inView = true;
  let pointerDown = false;
  let lastPointerDown = 0;

  const blocked = () => moving || focused || !inView || document.hidden;

  // A slide whose video is playing stays until the video has played through once (its own loop
  // continues afterwards). Only a video that is actually loaded holds: one skipped on a slow
  // connection, hidden at this breakpoint or under reduced motion does not. A loaded video that
  // has not started within HOLD_START_GRACE of the slide's turn being up, or that makes no
  // progress for HOLD_STALL, stops holding, so a struggling connection cannot park the carousel.
  const hold = { done: false, played: 0, lastTime: null, dueAt: null, lastProgressAt: 0 };
  const resetHold = () => {
    hold.done = false;
    hold.played = 0;
    hold.lastTime = null;
    hold.dueAt = null;
    hold.lastProgressAt = 0;
  };
  const activeVideo = () => {
    const slide = el.slides?.[el.current];
    if (!slide) return null;
    const wrapper = [...slide.querySelectorAll('mdl-hero-video')].find(
      (w) => w.dataset.video === 'loaded' && w.getClientRects().length > 0
    );
    return wrapper ? wrapper.querySelector('video') : null;
  };
  // Media events do not bubble, so listen in the capture phase for every video in the carousel.
  el.addEventListener(
    'timeupdate',
    (event) => {
      const video = /** @type {HTMLVideoElement} */ (event.target);
      if (hold.done || video !== activeVideo() || !(video.duration > 0)) return;
      const time = video.currentTime;
      if (hold.lastTime !== null) {
        const delta = time >= hold.lastTime ? time - hold.lastTime : video.duration - hold.lastTime + time;
        if (delta > 0) {
          hold.played += delta;
          hold.lastProgressAt = Date.now();
        }
      } else {
        hold.lastProgressAt = Date.now();
      }
      hold.lastTime = time;
      if (hold.played >= video.duration - 0.25) {
        hold.done = true;
        // The slide's turn was already up: move on now and give the next slide a full interval.
        if (hold.dueAt !== null && timer && !blocked()) {
          el.suspend();
          el.next();
          el.play();
        }
      }
    },
    true
  );
  const holding = () => {
    const video = activeVideo();
    if (!video || hold.done) return false;
    const now = Date.now();
    if (hold.dueAt === null) hold.dueAt = now;
    const playing = !video.paused && video.readyState >= 2;
    if (!playing) return now - hold.dueAt < HOLD_START_GRACE;
    return now - hold.lastProgressAt < HOLD_STALL;
  };
  el.addEventListener(SlideshowSelectEvent.eventName, resetHold);

  el.play = (interval = el.autoplayInterval) => {
    if (timer || !interval) return;
    el.paused = false;
    timer = setInterval(() => {
      if (!blocked() && !holding()) el.next();
    }, interval);
  };

  el.suspend = () => {
    clearInterval(timer);
    timer = undefined;
  };

  // The stock code pauses at the start of a drag and when the tab is hidden, then calls resume(),
  // which gives up while the sticky flag is set. Treat those two pauses as temporary; the pause
  // button and the theme editor still get the sticky one.
  el.pause = () => {
    if (pointerDown || document.hidden) {
      el.suspend();
      return;
    }
    el.paused = true;
    el.suspend();
  };

  const restart = () => {
    if (!el.autoplay || el.paused) return;
    el.suspend();
    el.play();
  };

  el.addEventListener('pointermove', (event) => {
    if (event.pointerType !== 'mouse') return;
    moving = true;
    clearTimeout(moveTimer);
    moveTimer = setTimeout(() => {
      moving = false;
    }, delay);
  });
  el.addEventListener('pointerleave', () => {
    clearTimeout(moveTimer);
    moving = false;
  });
  el.addEventListener('pointerdown', () => {
    pointerDown = true;
    lastPointerDown = Date.now();
  });
  document.addEventListener('pointerup', () => {
    pointerDown = false;
  });
  document.addEventListener('pointercancel', () => {
    pointerDown = false;
  });
  // Keyboard focus inside the carousel pauses it. A mouse click on an arrow leaves focus on the
  // button too, so focus that arrives within half a second of a pointer press does not count.
  let keyboardFocus = false;
  el.addEventListener('focusin', () => {
    keyboardFocus = Date.now() - lastPointerDown > 500;
    focused = keyboardFocus;
  });
  el.addEventListener('focusout', () => {
    setTimeout(() => {
      focused = keyboardFocus && el.matches(':focus-within');
    }, 0);
  });
  // Every change the visitor makes (arrow, dot, swipe, drag) restarts the countdown; the
  // slideshow's own select event carries the flag.
  el.addEventListener(SlideshowSelectEvent.eventName, (event) => {
    if (/** @type {SlideshowSelectEvent} */ (event).detail?.userInitiated) restart();
  });

  new IntersectionObserver(
    (entries) => {
      for (const entry of entries) inView = entry.intersectionRatio >= 0.5;
    },
    { threshold: [0, 0.5, 1] }
  ).observe(el);

  el.play();
}

const setupAll = () => document.querySelectorAll(SELECTOR).forEach((el) => setup(/** @type {any} */ (el)));

customElements.whenDefined('slideshow-component').then(() => {
  setupAll();
  document.addEventListener('shopify:section:load', setupAll);
});
